import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, Sparkles } from 'lucide-react';

const STORAGE_KEY = 'istudypro_focus_session_v1';

const readSession = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s || typeof s !== 'object') return null;
    if (!s.task) return null; // nothing meaningful to resume
    return s;
  } catch {
    return null;
  }
};

const fmt = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

/**
 * MinimizedFocusWidget
 *
 * Floating pill rendered on Dashboard when a focus session is mid-flight.
 * The actual timer pauses while the user is away (we don't burn battery on
 * a background tick) — the widget surfaces the *paused* state and a Resume
 * CTA. Tapping Resume jumps back to /focus where the saved state hydrates.
 *
 * Polls localStorage every 5s so dismissal in another tab is reflected.
 */
export default function MinimizedFocusWidget() {
  const navigate = useNavigate();
  const [session, setSession] = useState(readSession);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setSession(readSession()), 5000);
    return () => clearInterval(t);
  }, []);

  if (!session || dismissed) return null;

  const dismiss = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.96 }}
        transition={{ duration: 0.25 }}
        className="fixed bottom-6 right-6 z-40 bg-zinc-900 text-white rounded-2xl shadow-2xl border border-white/10 px-4 py-3 flex items-center gap-3 max-w-xs"
        data-testid="focus-resume-widget"
      >
        <div className="w-9 h-9 rounded-full bg-orange-500/15 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-orange-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-white/50 font-medium">Focus paused</p>
          <p className="text-sm font-medium truncate" data-testid="focus-resume-task">
            {session.task?.label || 'Pomodoro session'} · {fmt(session.secondsLeft || 0)}
          </p>
        </div>
        <button
          onClick={() => navigate('/focus')}
          className="bg-[#002FA7] hover:opacity-90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1 transition"
          data-testid="focus-resume-btn"
        >
          <Play className="w-3 h-3" />
          Resume
        </button>
        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white p-1"
          data-testid="focus-resume-dismiss"
          title="Dismiss session"
          aria-label="Dismiss focus session"
        >
          <X className="w-4 h-4" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
}
